/**
 * Reglas de alerta configurables (D-37). Las implementa la etapa 4. Torre, JWT del TMS, rol ADMIN.
 *
 *   GET    /configuracion/reglas-alerta?tipo=&ambito=      → ReglaAlerta[]
 *   POST   /configuracion/reglas-alerta       body ReglaAlertaBody              → ReglaAlerta
 *   PUT    /configuracion/reglas-alerta/:id   body ReglaAlertaActualizacionBody → ReglaAlerta
 *   DELETE /configuracion/reglas-alerta/:id   (no permitido para ámbito GENERAL: 409)
 *
 * Cada alerta se evalúa con la regla más específica que aplica: PARADA_RUTA → RUTA → UNIDAD_NEGOCIO → EMPRESA → GENERAL.
 * Una regla inactiva no genera alertas (tampoco cae a una menos específica). Hay una regla GENERAL por tipo, cargada
 * con los valores iniciales de las variables de entorno (CLAUDE.md, «parametros»).
 */
import type { FechaIso } from './comunes';
import type { TipoAlerta } from './alertas';

/** TODO(decision) (D-37): tres niveles; falta definir si alguno notifica fuera de la torre. */
export const NIVELES_ALERTA = ['INFORMATIVA', 'ADVERTENCIA', 'CRITICA'] as const;
export type NivelAlerta = (typeof NIVELES_ALERTA)[number];

export const AMBITOS_REGLA = ['GENERAL', 'EMPRESA', 'UNIDAD_NEGOCIO', 'RUTA', 'PARADA_RUTA'] as const;
export type AmbitoRegla = (typeof AMBITOS_REGLA)[number];

/**
 * Escalón de nivel. `desde` está en la unidad de la magnitud del tipo (ver `DESCRIPCION_REGLA_POR_TIPO`).
 * Nivel aplicado = el del último escalón con `desde <= magnitud` (los escalones van ordenados por `desde` ascendente);
 * si la magnitud no llega al primero, el del primero. Mientras la alerta está abierta el nivel puede subir, nunca bajar.
 * TODO(decision) (D-37): escalamiento por duración o magnitud, provisorio.
 */
export interface EscalonNivel {
  desde: number;
  nivel: NivelAlerta;
}

export interface ReglaAlerta {
  id: number;
  tipo: TipoAlerta;
  activa: boolean;
  ambito: AmbitoRegla;
  /**
   * Referencia del ámbito: null en GENERAL; el nombre en EMPRESA y UNIDAD_NEGOCIO (tal como viene en el viaje);
   * el id (como texto) de la ruta o de la parada de ruta en RUTA y PARADA_RUTA.
   */
  ambitoRef: string | null;
  /** Umbrales del tipo (claves y unidades en `DESCRIPCION_REGLA_POR_TIPO`). */
  parametros: Record<string, number>;
  /** Al menos un escalón. */
  niveles: EscalonNivel[];
  /** Nombre del usuario que la editó por última vez; null si nunca se editó (valores iniciales). */
  actualizadoPor: string | null;
  actualizadoEn: FechaIso;
}

/** POST /configuracion/reglas-alerta. No puede haber dos reglas con el mismo (tipo, ámbito, ambitoRef): 409. */
export interface ReglaAlertaBody {
  tipo: TipoAlerta;
  activa: boolean;
  ambito: AmbitoRegla;
  ambitoRef: string | null;
  parametros: Record<string, number>;
  niveles: EscalonNivel[];
}

/** PUT /configuracion/reglas-alerta/:id: tipo y ámbito no cambian. */
export type ReglaAlertaActualizacionBody = Pick<ReglaAlertaBody, 'activa' | 'parametros' | 'niveles'>;

export interface ParametroRegla {
  clave: string;
  unidad: 'min' | 'm' | 'km/h';
  descripcion: string;
}

export interface DescripcionRegla {
  /** Umbrales que usa el tipo. Vacío si la alerta nace de un hecho (sin umbral). */
  parametros: ParametroRegla[];
  /** Qué mide `EscalonNivel.desde` para este tipo. `unidad: null` = sin magnitud (un solo escalón con desde 0). */
  magnitud: { unidad: 'min' | 'm' | 'km/h' | null; descripcion: string };
}

/**
 * Parámetros y magnitud de los escalones por tipo. TODO(decision) (D-37): provisorio hasta confirmar con el cliente.
 * Los valores iniciales de la regla GENERAL salen de las variables de entorno indicadas.
 */
export const DESCRIPCION_REGLA_POR_TIPO: Record<TipoAlerta, DescripcionRegla> = {
  PARADA_NO_AUTORIZADA: {
    parametros: [{ clave: 'minutos', unidad: 'min', descripcion: 'Detenido al menos esto fuera de toda parada (PARADA_MIN)' }],
    magnitud: { unidad: 'min', descripcion: 'Minutos detenido' },
  },
  PARADA_NO_PLANIFICADA: {
    parametros: [],
    magnitud: { unidad: 'min', descripcion: 'Minutos de la detención de GPS que la confirma (0 si no se vio)' },
  },
  PARADA_OMITIDA: {
    parametros: [],
    magnitud: { unidad: null, descripcion: 'Sin magnitud: la parada obligatoria no se cumplió' },
  },
  PARADA_EXCEDIDA: {
    parametros: [{ clave: 'toleranciaMin', unidad: 'min', descripcion: 'Minutos de gracia sobre la duración máxima de la parada' }],
    magnitud: { unidad: 'min', descripcion: 'Minutos por encima de la duración máxima esperada' },
  },
  DESVIO_RUTA: {
    parametros: [{ clave: 'bufferM', unidad: 'm', descripcion: 'Distancia máxima a la traza de la ruta (DESVIO_BUFFER_M)' }],
    magnitud: { unidad: 'm', descripcion: 'Metros a la traza de la ruta asignada' },
  },
  CAMBIO_RUTA: {
    parametros: [{ clave: 'bufferM', unidad: 'm', descripcion: 'Distancia máxima a la traza de la otra ruta (DESVIO_BUFFER_M)' }],
    magnitud: { unidad: null, descripcion: 'Sin magnitud: el vehículo va por otra ruta activa de la troncal' },
  },
  EXCESO_VELOCIDAD: {
    parametros: [{ clave: 'maximaKmh', unidad: 'km/h', descripcion: 'Velocidad máxima (VELOCIDAD_MAX_KMH)' }],
    magnitud: { unidad: 'km/h', descripcion: 'Velocidad medida' },
  },
  RALENTI_EXCESIVO: {
    parametros: [{ clave: 'minutos', unidad: 'min', descripcion: 'Minutos en ralentí (RALENTI_MIN)' }],
    magnitud: { unidad: 'min', descripcion: 'Minutos en ralentí' },
  },
  INCONSISTENCIA_MARCACION: {
    parametros: [{ clave: 'reporteMaxMin', unidad: 'min', descripcion: 'Antigüedad máxima del reporte de Microtrack (MARCACION_REPORTE_MAX_MIN)' }],
    magnitud: { unidad: 'm', descripcion: 'Metros entre el teléfono y el último reporte de Microtrack' },
  },
  SIN_REPORTE: {
    parametros: [{ clave: 'minutos', unidad: 'min', descripcion: 'Minutos sin reportar con viaje en curso (SIN_REPORTE_MIN)' }],
    magnitud: { unidad: 'min', descripcion: 'Minutos sin reportar' },
  },
  INICIO_DEMORADO: {
    parametros: [{ clave: 'minutos', unidad: 'min', descripcion: 'Minutos de demora sobre la hora planificada (INICIO_DEMORADO_MIN)' }],
    magnitud: { unidad: 'min', descripcion: 'Minutos de demora' },
  },
  RETRASO_ETA: {
    parametros: [{ clave: 'toleranciaMin', unidad: 'min', descripcion: 'Tolerancia sobre la hora comprometida (ETA_TOLERANCIA_MIN)' }],
    magnitud: { unidad: 'min', descripcion: 'Minutos de demora estimada' },
  },
  RECURSO_INHABILITADO: {
    parametros: [],
    magnitud: { unidad: null, descripcion: 'Sin magnitud: chofer o vehículo inhabilitado en Mara' },
  },
  VIAJE_SIN_RUTA: {
    parametros: [],
    magnitud: { unidad: null, descripcion: 'Sin magnitud: la troncal del viaje no tiene rutas en el TMS' },
  },
};
